import Decimal from 'decimal.js';
import { comMoeda, ehDecimal, formatarArredondado, formatarFracaoPercentualCasas } from '@/lib/format';
import type { Celula, Coluna } from './tipos';

/** toString sem notacao exponencial (o Decimal padrao passa a 1e-7); a formatacao de exibicao mora em lib/format.ts. */
export const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -1000, toExpPos: 1000 });

const vazio = (v: Celula): boolean => v === null || v === undefined || v === '';

/** Soma de strings decimais (decimal.js; nunca float). Vazios contam 0. */
export function somar(valores: Celula[]): string {
  return valores.reduce<Decimal>((a, v) => (vazio(v) ? a : a.plus(String(v))), new D(0)).toString();
}

const casasDe = (c: Coluna): number => c.casas ?? (c.tipo === 'inteiro' ? 0 : 2);

/** "1234.5" -> "1.234,50" (pt-BR, arredonda HALF_UP; formatador unico de lib/format.ts). */
export function formatarBr(v: Celula, casas: number): string {
  if (vazio(v)) return '';
  const t = formatarArredondado(String(v), casas);
  return t === '—' ? String(v) : t;
}

/** Texto como aparece no PDF e nos testes. */
export function textoDaCelula(v: Celula, c: Coluna): string {
  if (vazio(v)) return '';
  // Coluna numerica com texto (ex.: 'Sem referência' no lugar do custo): sai como texto, sem R$ nem casas.
  if ((c.tipo === 'moeda' || c.tipo === 'percentual' || c.tipo === 'numero' || c.tipo === 'inteiro') && !ehDecimal(String(v))) return String(v);
  switch (c.tipo) {
    case 'moeda':
      return comMoeda(formatarBr(v, casasDe(c)));
    case 'percentual': {
      const t = formatarFracaoPercentualCasas(String(v), c.casas ?? 2);
      return t === '—' ? String(v) : t;
    }
    case 'inteiro':
    case 'numero':
      return formatarBr(v, casasDe(c));
    case 'data': {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
      return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
    }
    default:
      return String(v);
  }
}

/** Valor numerico da celula do Excel (o Excel guarda double): conversao da string decimal, sem conta. */
export function paraNumeroExcel(v: Celula, c: Coluna): number | null {
  if (vazio(v)) return null;
  const d = new D(String(v));
  void c;
  return Number(d.toString());
}

/** Formato do Excel por tipo (separadores seguem o idioma do Excel do usuario: pt-BR mostra 1.234,50). */
export function formatoExcel(c: Coluna): string | undefined {
  const casas = casasDe(c);
  const zeros = casas > 0 ? `.${'0'.repeat(casas)}` : '';
  switch (c.tipo) {
    case 'moeda':
      return `"R$" #,##0${zeros}`;
    case 'percentual':
      return `0${(c.casas ?? 2) > 0 ? `.${'0'.repeat(c.casas ?? 2)}` : ''}%`;
    case 'numero':
    case 'inteiro':
      return `#,##0${zeros}`;
    case 'data':
      return 'dd/mm/yyyy';
    default:
      return undefined;
  }
}

/** Linha de totais da secao: a da API, ou a soma (decimal.js) das colunas marcadas `somar`. */
export function totaisDaSecao(colunas: Coluna[], linhas: Array<Record<string, Celula>>, prontos?: Record<string, Celula>): Record<string, Celula> | null {
  if (prontos) return prontos;
  const marcadas = colunas.filter((c) => c.somar);
  if (!marcadas.length || !linhas.length) return null;
  const out: Record<string, Celula> = {};
  for (const c of marcadas) out[c.chave] = somar(linhas.map((l) => l[c.chave]));
  return out;
}

export const nomeArquivoSeguro = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'relatorio';

/** Todo o texto do relatorio (titulos de coluna e celulas como aparecem): usado nos testes de permissao ("o arquivo nao tem custo"). */
export function textoCompleto(rel: { titulo: string; secoes: Array<{ colunas: Coluna[]; linhas: Array<Record<string, Celula>>; totais?: Record<string, Celula> }> }): string {
  const partes: string[] = [rel.titulo];
  for (const s of rel.secoes) {
    partes.push(...s.colunas.map((c) => c.titulo));
    for (const l of s.linhas) partes.push(...s.colunas.map((c) => textoDaCelula(l[c.chave], c)));
    if (s.totais) partes.push(...s.colunas.map((c) => textoDaCelula(s.totais?.[c.chave], c)));
  }
  return partes.join('\n');
}
